import { ComputedOrImmediate } from '@funkey/shared';
import type * as Sentry from '@sentry/nextjs';
import { Duration } from 'effect';

type Sentry_InitOptions = Parameters<typeof Sentry.init>[0];

function Sentry_mergeConfigs(
    ...configs: readonly ComputedOrImmediate<() => Partial<Sentry_InitOptions>>[]
): Sentry_InitOptions {
    // let definedKeys = new Set<string>();
    const config = {} as Sentry_InitOptions;

    for (const _x of configs) {
        const x = ComputedOrImmediate.get(_x);
        // const keys = new Set(Object.keys(x));
        // const duplicates = definedKeys.intersection(keys);
        // if (duplicates.size !== 0) {
        //     throw new Error(
        //         `Sentry config error: the following config fields were defined multiple times "${JSON.stringify(Array.from(duplicates.values()))}"`,
        //     );
        // }
        //
        // definedKeys = definedKeys.union(keys);

        Object.assign(config, x);
    }

    return config;
}

namespace Sentry_Tracing {
    const NON_ERROR_SAMPLE_RATE = 0.1;
    const STALE_TRACE_TTL_MS = Duration.toMillis(Duration.minutes(5));

    // --- Tail-based sampling ---
    // Record all traces locally (tracesSampleRate: 1), but only send a fraction to Sentry.
    // Traces that contain errors are always sent so the full span waterfall is available
    // when investigating issues. Non-error traces are sampled at a low rate.

    /** Tracks trace IDs that had at least one error, so `beforeSendTransaction` can keep them. */
    const errorTraceIds = new Map<string, number>();

    export function createConfig() {
        return {
            // Record all traces locally so we can make tail-based sampling decisions
            // in beforeSendTransaction. Actual send rate is controlled below.
            tracesSampleRate: 1,
            profilesSampleRate: 0.1,

            beforeSend(event) {
                // Track trace IDs that had errors so their transactions are always sent.
                const traceId = event.contexts?.trace?.trace_id;
                if (traceId) {
                    errorTraceIds.set(traceId, Date.now());
                }
                return event;
            },

            beforeSendTransaction(event) {
                // Evict stale entries to prevent unbounded growth
                const now = Date.now();
                for (const [id, timestamp] of errorTraceIds) {
                    if (now - timestamp > STALE_TRACE_TTL_MS) errorTraceIds.delete(id);
                }

                const traceId = event.contexts?.trace?.trace_id;

                // Always send traces that contain errors
                if (traceId && errorTraceIds.has(traceId)) {
                    errorTraceIds.delete(traceId);
                    return event;
                }

                // Sample non-error traces
                return Math.random() < NON_ERROR_SAMPLE_RATE ? event : null;
            },
        } satisfies Partial<Sentry_InitOptions>;
    }
}

namespace Sentry_Logging {
    const ANSI_REGEX = /\x1b\[[\d;]*m/g;

    export function createConfig() {
        return {
            beforeBreadcrumb(breadcrumb) {
                if (breadcrumb.category === 'console') {
                    // Strip ANSII coloring from the logs

                    if (typeof breadcrumb.message === 'string') {
                        breadcrumb.message = stripAnsi(breadcrumb.message);
                    }
                    if (Array.isArray(breadcrumb.data?.arguments)) {
                        breadcrumb.data.arguments = breadcrumb.data.arguments.map((arg: unknown) =>
                            typeof arg === 'string' ? stripAnsi(arg) : arg,
                        );
                    }
                }
                return breadcrumb;
            },
        } satisfies Partial<Sentry_InitOptions>;
    }

    function stripAnsi(str: string): string {
        return str.replace(ANSI_REGEX, '');
    }
}

namespace Sentry_Env {
    const SENTRY_DSN =
        'https://e67db42fe97a10cb1be71f66a2f6ad97@o4507542413312000.ingest.de.sentry.io/4507542430351440';

    export type Options = Pick<Sentry_InitOptions, 'dsn' | 'enabled' | 'environment' | 'release'>;

    export function createVercelConfig(): Sentry_Env.Options {
        /** Enable on vercel preview environments */
        const ENABLE_PREVIEW = false;

        return {
            /** Public key
             * Used to create the `SENTRY_AUTH_TOKEN` env variable in `.env.sentry-build-plugin` at runtime time. */
            dsn: SENTRY_DSN,
            /** Only the NEXT_PUBLIC_ variant reaches the browser bundle; the server sees the bare one. */
            enabled:
                (process.env.VERCEL_ENV || process.env.NEXT_PUBLIC_VERCEL_ENV) === 'production'
                    ? true
                    : ENABLE_PREVIEW,

            environment: (() => {
                // If we're a vercel deployment

                /** @example '[project=funkey-web]-git-*-[team=funkey].vercel.app' */
                const branchURL =
                    process.env.VERCEL_BRANCH_URL || process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL;

                if (!branchURL) return `local-${process.env.NODE_ENV}`;

                const BRANCH_PREFIX = '-git-';
                const VERCEL_SUFFIX = '-funkey.vercel.app';

                const begin = branchURL.indexOf(BRANCH_PREFIX);
                const end = branchURL.indexOf(VERCEL_SUFFIX);

                if (begin < 0 || end < 0) {
                    // prettier-ignore
                    throw new Error(`env.GIT_BRANCH_SLUG parsing error: "${branchURL}" does not match the pattern of "*${BRANCH_PREFIX}[branchSlug]${VERCEL_SUFFIX}"`)
                }

                const slug = branchURL.slice(begin + BRANCH_PREFIX.length, end);
                return slug;
            })(),

            release: process.env.VERCEL_GIT_COMMIT_SHA,
        };
    }

    export function createLocalConfig(): Sentry_Env.Options {
        /** Enable on local development environment */
        const ENABLE_LOCAL = false;

        return {
            // Set this to true if you want to enable Sentry while developing
            dsn: SENTRY_DSN,
            enabled: ENABLE_LOCAL,
            environment: (() => {
                const identifier: string = process.env.USER || process.env.NODE_ENV;

                return `local-${identifier}`;
            })(),
            release: undefined,
        };
    }

    export function createPlaygroundConfig(): Sentry_Env.Options {
        return {
            // Sentry Playground DSN
            dsn: 'https://c51f763d8ba60703cf678206e06d2d41@o4509683960643584.ingest.us.sentry.io/4510832752066560',

            // Setting this option to true will print useful information to the console while you're setting up Sentry.
            enabled: true,
            environment: 'web-sentry-playground',
            release: undefined,
        };
    }
}

// Needs to be at the bottom because namespaces don't get hoisted
export const SENTRY_SHARED_CONFIG: Sentry_InitOptions = Sentry_mergeConfigs(
    Sentry_Tracing.createConfig(),
    Sentry_Logging.createConfig(),
    (): Sentry_Env.Options => {
        // Sentry Playground
        // return Sentry_Env.createPlaygroundConfig();

        if (
            process.env.NODE_ENV === 'development' ||
            // If we're on a local maching running npm run dev
            (process.env.NODE_ENV &&
                (process.env.NEXT_PUBLIC_VERCEL_ENV === undefined ||
                    process.env.NEXT_PUBLIC_VERCEL_ENV === 'local'))
        ) {
            return Sentry_Env.createLocalConfig();
        }

        return Sentry_Env.createVercelConfig();
    },
    {
        ignoreErrors: ["Can't find variable: gmo"],
        // Setting this option to true will print useful information to the console while you're setting up Sentry.
        debug: false,
        sendDefaultPii: true,
    },
);
